import moment from 'moment';
import hasIn from 'lodash/hasIn';
import store from '@/store/store';
import { createAllAsync, getAll, updateAllAsync } from '@/api/devis/ligneCommandeRecommandationDevis';
import { getAllProduitsSousContrat, browseClients } from '@/api/clients/index';
import { formatDate, adressFullStringGetter } from '@/services/general/general';
import genererPanneau, { genererBudget, genererBudgetGeneration } from '@/services/gestionPanneau/genererPanneau';
import { calcPrixTotal } from '@/services/gestionPanneau/calcPanneau';
import { WITHOUT_LOGO } from '@/configuration/clientConf';
import { CATEGORIE_UNITAIRE } from '@/configuration/produitConf';
import { getPJsLogoByIds } from '@/services/piecesJointes/getPiecesJointeLogoByIds';
import { isLigneHonoraires, isLigneLocation, isLigneTlpe, isLigneHonoraireFraisTraitement } from '@/services/devisCommandes/echeanciers/gestionEcheancier';
import { getCategorieFromProduit } from '@/services/general/genererReferenceProduit';
import { quantiteDispoToPourcentageSurface } from '@/services/gestionPanneau/surfacePanneau';

const LIGNE_DEFAULT_DATA = { totalHtAvecLigneRemise: 0 };

const getValueOrDefault = (value, defaultValue) => {
  return value ? value : defaultValue;
};
const trouverLigneParLibelle = (lignes, libelle) => {
  const find = lignes.find(ligne => ligne.libelle.toLowerCase().includes(libelle));
  return getValueOrDefault(find, LIGNE_DEFAULT_DATA);
};
const getRegie = (ligneCommandes, fournisseurs) => {
  const ligne = ligneCommandes.find(el => !!el.fournisseurId);
  if (!ligne) return '';
  const find = fournisseurs.find(f => f.id === ligne.fournisseurId);
  if (find)  return find.nomCommercial || '';
  return '';
};
const getAdresse = (adresse) => {
  const lat = getValueOrDefault(adresse.latitude, 0);
  const lng = getValueOrDefault(adresse.longitude, 0);
  return {
    ...adresse,
    libelle: adresse.adresse1,
    coordonnees: { lat, lng },
  };
};
const isInCurrentYear = (date) => {
  return moment(date) >= moment().startOf('year') && moment(date) <= moment().endOf('year');
};
const getFournisseursAsync = async (lignes) => {
  const allFournisseursIds = lignes
    .flatMap(el => el.lignesCommande)
    .map(el => el.fournisseurId)
    .filter(Boolean);
  if (!allFournisseursIds.length) return [];
  const societeId = store.getters['societe/societeActiveId'];
  const fournisseurs = (await browseClients(WITHOUT_LOGO, {
    societeId,
    tiersId: allFournisseursIds,
  })).data;
  return fournisseurs;
}

const ligneCommandeToPanneau = (ligne, fournisseurs) => {
  const { lignesCommande, produit, dateDebut, dateFin, echeanciers, devisCommande } = ligne;
  const ligneLocation = lignesCommande.find(l => isLigneLocation(l)) || LIGNE_DEFAULT_DATA;
  if (!ligneLocation) return null;

  const societe = store.getters['societe/societeActive'];
  const ligneTlpe = lignesCommande.find(l => isLigneTlpe(l, societe)) || LIGNE_DEFAULT_DATA;
  const ligneHonoFraisTraitement = lignesCommande.find(l => isLigneHonoraireFraisTraitement(l, societe)) || LIGNE_DEFAULT_DATA;
  // La ligne d'honoraires frais de traitement ne doit pas être reprise en honoraires ni en frais techniques
  const lignesHorsFraisTraitement = lignesCommande.filter(l => !isLigneHonoraireFraisTraitement(l, societe));
  const ligneHono = lignesHorsFraisTraitement.find(l => isLigneHonoraires(l, societe)) || LIGNE_DEFAULT_DATA;
  const ligneFrais = trouverLigneParLibelle(lignesHorsFraisTraitement, 'frais');

  const categorie = getCategorieFromProduit(lignesCommande);
  const regie = getRegie(lignesCommande, fournisseurs);

  const { adresse, materielType, format, azimut, dateDemontage, disposition } = produit.produitFace;
  const periode = `${formatDate(dateDebut)} au ${formatDate(dateFin)}`;
  const adresseCopy = getAdresse(adresse);

  return {
    ...genererPanneau(),
    regie,
    format,
    categorie,
    id: produit.id,
    adresse: adresseCopy,
    ancienPanneau: 'OUI',
    referencePanneau: produit.reference,
    materiel: materielType,
    disposition: disposition,
    referenceConfrere: produit.referenceConfrere,
    periodeContrat: periode,
    echeanciers: echeanciers,
    estPanneauSousContrat: true,
    pourcentageSurface: quantiteDispoToPourcentageSurface(ligne.quantiteDispo),
    ligneCommandeId: ligne.id,
    azimut: getValueOrDefault(azimut, 0),
    renouvellement: devisCommande ? devisCommande.motifNonRenouvellement : null,
    etatRenouvellement: devisCommande ? devisCommande.etatRenouvellement : null,
    adresseCommerciale: adressFullStringGetter(adresseCopy),
    srcImg: getValueOrDefault(ligne.cheminProduit, ligne.cheminMinProduit),
    budgetGeneration: (() => {
      const bg = genererBudgetGeneration();
      bg.dateDebut = new Date(dateDebut);
      bg.dateFin = new Date(dateFin);
      bg.prixBrutNegocie = getValueOrDefault(ligneLocation.prixUnitaire, 0);
      bg.prixNet = ligneLocation.totalHtAvecLigneRemise;
      bg.tlpe = ligneTlpe.totalHtAvecLigneRemise;
      bg.fraisTechnique = ligneFrais.totalHtAvecLigneRemise;
      bg.honoraires = ligneHono.totalHtAvecLigneRemise;
      bg.honoraireFraisTraitement = ligneHonoFraisTraitement.totalHtAvecLigneRemise;
      return bg;
    })(),
    budgets: (() => {
      const budget = genererBudget();
      budget.dateDebut = new Date(dateDebut);
      budget.dateFin = new Date(dateFin);
      budget.prixBrutNegocie = getValueOrDefault(ligneLocation.prixUnitaire, 0);
      budget.prixNet = ligneLocation.totalHtAvecLigneRemise;
      budget.tlpe = ligneTlpe.totalHtAvecLigneRemise;
      budget.fraisTechnique = ligneFrais.totalHtAvecLigneRemise;
      budget.honoraires = ligneHono.totalHtAvecLigneRemise;
      budget.honoraireFraisTraitement = ligneHonoFraisTraitement.totalHtAvecLigneRemise;
      budget.ligneCommandeId = ligne.id;
      budget.prixTotal = calcPrixTotal(budget);
      return [budget];
    })(),
    dateDemontage,
  };
}

const recupererPanneauxSousContrat = async (pointDeVenteId, panneaux) => {
  const query = {
    tiersId: [pointDeVenteId],
    typesProduits: [CATEGORIE_UNITAIRE],
    excludeIds: panneaux
      .map(el => el.ligneCommandeId).filter(Boolean),
  };
  const { items } = (await getAllProduitsSousContrat(query)).data.result;
  const lignesCommandes = items.filter(produit => {
    return produit.ligneCommandeRegroupement.echeanciers.length > 0
      && produit.ligneCommandeRegroupement.echeanciers.some(ech => isInCurrentYear(ech.dateDebut))
      && isInCurrentYear(produit.ligneCommandeRegroupement.dateDebut)
  });
  const lignesCommandeMap = lignesCommandes
    .map(l => ({
      ...l.ligneCommandeRegroupement,
      cheminProduit: l.cheminProduit,
      cheminMinProduit: l.cheminMinProduit,
    }));
  const fournisseurs = await getFournisseursAsync(lignesCommandeMap);
  return lignesCommandeMap
    .map(ligne => ligneCommandeToPanneau(ligne, fournisseurs))
    .filter(Boolean);
};

const recupererPanneauxSousContratDevis = async (recommandation) => {
  const panneaux = recommandation.panneauxSousContrat;
  if (!recommandation.id) return panneaux;
  const result = (await getAll(recommandation.id)).data.result;
  if (!result.length) return panneaux;
  const fournisseurs = await getFournisseursAsync(result.map(r => r.ligneCommande));
  const pieceJointes = await getPJsLogoByIds(result.map(r => r.ligneCommande.produit.id));
  const panneauxSousContrat = result.map(res => {
    const panneau = ligneCommandeToPanneau(res.ligneCommande, fournisseurs);
    panneau.actif = res.prisEnCompte;
    const pj = pieceJointes.find(p => p.elementId === panneau.id);
    panneau.srcImg = pj ? pj.chemin : null;
    panneau.ligneRecommandationDevisId = res.id;
    return panneau;
  });
  const panneauxSousContratFilter = panneauxSousContrat
    .filter(p => !panneaux.map(pn => pn.id).includes(p.id));
  return [
    ...panneauxSousContratFilter,
    ...panneaux,
  ];
}

export default async function gestionPanneauSousContrat(recommandation) {
  if (!hasIn(recommandation, 'pointDeVente.id')) return [];
  const panneauxSxContratDevis = await recupererPanneauxSousContratDevis(recommandation);
  const panneauxSxContrat = await recupererPanneauxSousContrat(
    recommandation.pointDeVente.id, panneauxSxContratDevis);
  return [
    ...panneauxSxContratDevis,
    ...panneauxSxContrat,
  ];
}

export async function savePanneauxSousContrat(panneaux, devisId) {
  if (!panneaux.length) return;
  const lignesToCreate = panneaux.filter(p => !p.ligneRecommandationDevisId).map(p => ({
    prisEnCompte: p.actif,
    ligneCommandeId: p.ligneCommandeId,
  }));
  const lignesToUpdate = panneaux.filter(p => !!p.ligneRecommandationDevisId).map(p => ({
    devisId,
    prisEnCompte: p.actif,
    ligneCommandeId: p.ligneCommandeId,
    id: p.ligneRecommandationDevisId,
  }));
  if (lignesToCreate.length) await createAllAsync({ devisId, lignes: lignesToCreate });
  if (lignesToUpdate.length) await updateAllAsync({ devisId, lignes: lignesToUpdate });
}
